import { make } from '@/editorjs/utils';

export default class BlockStyleTune {
    /**
     * Tune의 정적 getter는 Tune 메뉴에 아이콘과 제목을 표시하기 위해 필요합니다.
     */
    static get isTune() {
        return true;
    }

    /**
     * @param {{api: object, data: object, config: object, block: object}}
     */
    constructor({ api, data = {}, block }) {
        this.api = api;
        this.block = block;

        this.fourSidedProps = ['padding', 'margin', 'borderRadius', 'borderWidth'];
        this.borderProps = ['borderWidth', 'borderStyle', 'borderColor'];

        this.data = this.migrateData(data); // 데이터 구조 초기화/마이그레이션

        // 테두리 관련 설정
        this.BORDER_DEFAULTS = { width: '1', style: 'solid', color: '#000000' };
        this.BORDER_STYLE_OPTIONS = {
            '': '선택...', 'solid': '실선', 'dashed': '파선', 'dotted': '점선',
            'double': '이중선', 'groove': '입체 (오목)',
        };

        this.wrapper = undefined;
        this.blockWrapper = undefined;
        this.inputs = {}; // DOM 요소 참조를 저장할 객체
    }

    /**
     * ✨ 2. 데이터 구조를 현대화하고, 이전 버전 데이터(단일 값)를 호환합니다.
     * e.g.) 'padding: "10"' -> 'padding: { top: "10", right: "10", bottom: "10", left: "10" }'
     */
    migrateData(data) {
        const newData = {};

        this.fourSidedProps.forEach(prop => {
            // borderRadius의 경우, 이전 키(top, right 등)를 새로운 키(topLeft 등)로 매핑합니다.
            if (prop === 'borderRadius' && typeof data[prop] === 'object' && data[prop] !== null) {
                newData[prop] = {
                    topLeft: data[prop].topLeft || data[prop].top || '',
                    topRight: data[prop].topRight || data[prop].right || '',
                    bottomRight: data[prop].bottomRight || data[prop].bottom || '',
                    bottomLeft: data[prop].bottomLeft || data[prop].left || '',
                };
            } else if (typeof data[prop] === 'object' && data[prop] !== null) {
                newData[prop] = data[prop];
            } else if (data[prop]) {
                const value = data[prop];
                // borderRadius는 새로운 키 구조로 변환
                if (prop === 'borderRadius') {
                    newData[prop] = { topLeft: value, topRight: value, bottomRight: value, bottomLeft: value };
                } else {
                    newData[prop] = { top: value, right: value, bottom: value, left: value };
                }
            } else {
                newData[prop] = {};
            }
        });

        Object.keys(data).forEach(key => {
            if (!this.fourSidedProps.includes(key)) {
                newData[key] = data[key];
            }
        });

        return newData;
    }


    /**
     * 블록을 감싸는 wrapper를 만들고 스타일을 적용합니다.
     */
    wrap(blockContent) {
        const wrapper = make('div');
        this.applyStyle(wrapper);
        wrapper.appendChild(blockContent);
        this.blockWrapper = wrapper;
        return wrapper;
    }

    render() {
        return [{
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z" /><path d="M12 8l0 .01" /><path d="M8 12l0 .01" /><path d="M12 12l0 .01" /><path d="M16 12l0 .01" /><path d="M12 16l0 .01" /></svg>`,
            title: '블록 스타일',
            children: {
                items: this.createStyleSettingItems()
            },
            closeOnActivate: false,
        }, {
            type: "separator"
        }];
    }

    /**
     * 스타일 설정 메뉴 아이템들을 생성합니다.
     */
    createStyleSettingItems() {
        const settings = [
            { key: 'padding', type: 'four-sided', unit: 'px', label: '패딩' },
            { key: 'margin', type: 'four-sided', unit: 'px', label: '마진' },
            { type: 'separator' },
            { key: 'borderRadius', type: 'four-sided', unit: 'px', label: '모서리 둥글기' },
            { key: 'borderWidth', type: 'four-sided', unit: 'px', label: '테두리 두께' },
            { key: 'borderStyle', type: 'select', label: '테두리 스타일', options: this.BORDER_STYLE_OPTIONS },
            { key: 'borderColor', type: 'color', label: '테두리 색' },
            { type: 'separator' },
            { key: 'textColor', type: 'color', label: '글자색' },
            { key: 'backgroundColor', type: 'color', label: '배경색' },
            { type: 'separator' },
            { type: 'reset', label: '스타일 초기화' }
        ];

        return settings.map(setting => this.createSettingItem(setting));
    }

    /**
     * 설정 타입에 따라 적절한 입력 요소를 생성하는 래퍼 함수입니다.
     */
    createSettingItem(setting) {
        if (setting.type === 'separator') return { type: 'separator' };
        if (setting.type === 'reset') return this.createResetButton(setting);

        let element;
        const commonEventHandler = (key, value) => this.handleValueChange(key, value);

        switch (setting.type) {
            case 'four-sided':
                element = this.createFourSidedInput(setting.key, setting.label, setting.unit);
                break;
            case 'color':
                element = this.createColorInput(setting.key, setting.label, commonEventHandler);
                break;
            case 'select':
                element = this.createSelectInput(setting.key, setting.label, setting.options, commonEventHandler);
                break;
            // 다른 타입이 필요하면 여기에 추가
        }

        return { type: 'html', element, title: setting.label };
    }

    createFourSidedInput(key, label, unit) {
        this.inputs[key] = {}; // 해당 속성의 input 참조를 저장할 객체 초기화
        const data = this.data[key] || {};
        const isBorderRadius = key === 'borderRadius';
        const sides = isBorderRadius
            ? ['topLeft', 'topRight', 'bottomRight', 'bottomLeft']
            : ['top', 'right', 'bottom', 'left'];

        const icons = {
            // 일반 4방향 아이콘
            top: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M4.182 6.036L7.5 2.718l3.318 3.318H4.182z" fill="currentColor"></path></svg>`,
            right: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M8.964 4.182L12.282 7.5 8.964 10.818V4.182z" fill="currentColor"></path></svg>`,
            bottom: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M10.818 8.964L7.5 12.282 4.182 8.964h6.636z" fill="currentColor"></path></svg>`,
            left: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M6.036 10.818L2.718 7.5 6.036 4.182v6.636z" fill="currentColor"></path></svg>`,
            // borderRadius 전용 아이콘
            topLeft: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M7 1C3.686 1 1 3.686 1 7V1H7Z" fill="currentColor"/></svg>`,
            topRight: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M8 1H14V7C14 3.686 11.314 1 8 1Z" fill="currentColor"/></svg>`,
            bottomRight: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M8 14C11.314 14 14 11.314 14 8V14H8Z" fill="currentColor"/></svg>`,
            bottomLeft: `<svg width="16" height="16" viewBox="0 0 15 15"><path d="M7 14H1V8C1 11.314 3.686 14 7 14Z" fill="currentColor"/></svg>`,
        };

        // 전체 컨테이너
        const container = make('div', 'maily-editor-utils--four-sided-container');
        const labelEl = make('label', 'maily-editor-utils--four-sided-label', { textContent: `${label} (${unit})` });

        // 토글 버튼 + 입력 필드를 감싸는 래퍼
        const contentWrapper = make('div', 'maily-editor-utils--four-sided-content-wrapper');

        // 단일 입력 필드 (이제 wrapper 없이 바로 생성)
        const singleInput = make('input', ['maily-editor-utils--input-default', 'w-full'], {
            type: 'number',
            min: 0,
            placeholder: '전체',
        });
        this.inputs[key].single = singleInput;

        singleInput.addEventListener('input', (e) => {
            const value = e.target.value;
            const newValues = {};
            sides.forEach(side => {
                newValues[side] = value; // 올바른 키 이름으로 할당
                if (this.inputs[key][side]) this.inputs[key][side].value = value;
            });
            this.handleValueChange(key, newValues);
        });

        // 개별 입력 필드(2x2 그리드)를 담는 컨테이너
        const detailsContainer = make('div', 'maily-editor-utils--four-sided-details');
        sides.forEach(side => {
            const inputId = `maily-style-tune-${key}-${side}-${this.block.id}`;
            const sideInputWrapper = make('div', 'maily-editor-utils--four-sided-side-input-wrapper');
            const iconLabel = make('label', 'maily-editor-utils--four-sided-side-icon', {
                innerHTML: icons[side],
                htmlFor: inputId, // input의 id와 연결
            });
            const sideInput = make('input', 'maily-editor-utils--four-sided-side-input', {
                type: 'number',
                min: 0,
                placeholder: '-',
                value: data[side] || '',
                id: inputId
            });

            this.inputs[key][side] = sideInput;

            sideInput.addEventListener('input', (e) => {
                // 현재 데이터 복사 후 변경된 값만 업데이트
                const newValues = { ...(this.data[key] || {}) };
                newValues[side] = e.target.value;

                singleInput.value = ''; // 개별 입력 시 단일 입력 필드 비움

                // 범용 핸들러 호출하여 데이터 변경 및 테두리 동기화 로직 실행
                this.handleValueChange(key, newValues);
            });

            sideInputWrapper.append(iconLabel, sideInput);
            detailsContainer.appendChild(sideInputWrapper);
        });

        // 펼치기/접기 버튼
        const toggleIcon = isBorderRadius
            ? `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v2" /><path d="M20 16v2a2 2 0 0 1 -2 2h-2" /><path d="M8 20h-2a2 2 0 0 1 -2 -2v-2" /><path d="M4 8v-2a2 2 0 0 1 2 -2h2" /></svg>`
            : `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8v8" /><path d="M20 16v-8" /><path d="M8 4h8" /><path d="M8 20h8" /></svg>`;

        const toggleButton = make('button', 'maily-editor-utils--four-sided-toggle-button', {
            innerHTML: toggleIcon,
            type: 'button',
        });


        // 초기 상태 결정
        const isUniform = sides.every(s => data[s] && data[s] === data[sides[0]]);
        let isExpanded = !isUniform && Object.values(data).some(v => v);

        const setVisibility = () => {
            detailsContainer.style.display = isExpanded ? 'grid' : 'none';
            singleInput.style.display = isExpanded ? 'none' : 'block';
        };

        if (isUniform && data[sides[0]]) {
            singleInput.value = data[sides[0]];
        }

        setVisibility(); // 초기 가시성 설정

        toggleButton.addEventListener('click', () => {
            isExpanded = !isExpanded;
            setVisibility();
            let newValues = { ...(this.data[key] || {}) };
            if (isExpanded) {
                const singleValue = singleInput.value;
                if (singleValue) {
                    sides.forEach(side => newValues[side] = singleValue);
                }
            } else {
                // ✨ 접을 때는 첫 번째 키(topLeft 또는 top)를 기준으로 통일
                const firstValue = this.inputs[key][sides[0]].value;
                if (firstValue) {
                    sides.forEach(side => newValues[side] = firstValue);
                }
            }
            this.handleValueChange(key, newValues);
        });

        contentWrapper.append(singleInput, detailsContainer, toggleButton);
        container.append(labelEl, contentWrapper);

        return container;
    }

    createColorInput(key, label, eventHandler) {
        const container = make('div', 'maily-editor-utils--input-container');
        const labelEl = make('label', 'maily-editor-utils--input-label', { textContent: label });
        const inputWrapper = make('div', 'flex items-center w-full');

        const textInput = make('input', 'maily-editor-utils--input-color-text', {
            type: 'text',
            value: this.data[key] || '',
            placeholder: '#000000',
        });
        const colorInput = make('input', 'maily-editor-utils--input-color-picker', {
            type: 'color',
            value: this.data[key] || '#ffffff',
        });

        if (this.borderProps.includes(key)) {
            this.inputs[key] = { text: textInput, color: colorInput };
        }

        textInput.addEventListener('input', (e) => {
            colorInput.value = e.target.value || '#ffffff';
            eventHandler(key, e.target.value);
        });
        colorInput.addEventListener('input', (e) => {
            textInput.value = e.target.value;
            eventHandler(key, e.target.value);
        });

        inputWrapper.append(textInput, colorInput);
        container.append(labelEl, inputWrapper);
        return container;
    }

    createSelectInput(key, label, options, eventHandler) {
        const container = make('div', 'maily-editor-utils--input-container');
        const labelEl = make('label', 'maily-editor-utils--input-label', { textContent: label });
        const selectEl = make('select', 'maily-editor-utils--input-default');

        if (this.borderProps.includes(key)) {
            this.inputs[key] = { select: selectEl };
        }

        Object.entries(options).forEach(([value, text]) => {
            const optionEl = make('option', null, { value, textContent: text });
            if (this.data[key] === value) optionEl.selected = true;
            selectEl.appendChild(optionEl);
        });

        selectEl.addEventListener('change', (e) => eventHandler(key, e.target.value));

        container.append(labelEl, selectEl);
        return container;
    }

    createResetButton(setting) {
        return {
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7l16 0" /><path d="M10 11l0 6" /><path d="M14 11l0 6" /><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /></svg>`,
            title: setting.label,
            hint: { title: "이 블록에 설정 된 블록 스타일이 전부 초기화 됩니다." },
            closeOnActivate: true,
            onActivate: () => this.resetStyle(),
        };
    }

    handleValueChange(key, value) {
        this.data[key] = value;

        if (this.borderProps.includes(key)) {
            // ✨ 변경된 키와 값을 함께 전달하여 "삭제" 의도를 파악할 수 있게 함
            this.syncBorderProperties(key, value);
        }

        this.updateAndSave();
    }

    syncBorderProperties(changedKey, changedValue) {
        // 1. 사용자의 액션이 "삭제"인지 명확하게 판단합니다.
        // - 값이 없거나 ('' 등)
        // - 객체인데 모든 내부 값이 비어있는 경우
        const isDeletion = !changedValue ||
            (typeof changedValue === 'object' && Object.values(changedValue).every(v => !v));

        if (isDeletion) {
            // 2. "삭제"가 맞다면, 다른 것을 확인하지 않고 즉시 모든 테두리 데이터를 비웁니다.
            this.data.borderWidth = {};
            this.data.borderStyle = '';
            this.data.borderColor = '';

            // 3. UI를 업데이트하고 함수를 즉시 종료합니다. (가장 중요)
            this.updateBorderInputsUI();
            return;
        }

        // 4. "삭제"가 아니라면, "추가/변경" 로직을 수행합니다.
        //    (이 부분은 기존 로직과 동일)
        const hasBorderWidth = this.data.borderWidth && Object.values(this.data.borderWidth).some(v => v);
        const hasBorderStyle = !!this.data.borderStyle;
        const hasBorderColor = !!this.data.borderColor;

        // 현재 어떤 값이든 하나라도 존재하면, 나머지를 기본값으로 채웁니다.
        if (hasBorderWidth || hasBorderStyle || hasBorderColor) {
            if (!hasBorderStyle) {
                this.data.borderStyle = this.BORDER_DEFAULTS.style;
            }
            if (!hasBorderColor) {
                this.data.borderColor = this.BORDER_DEFAULTS.color;
            }
            if (!hasBorderWidth) {
                const defaultWidth = this.BORDER_DEFAULTS.width;
                this.data.borderWidth = { top: defaultWidth, right: defaultWidth, bottom: defaultWidth, left: defaultWidth };
            }
        }

        // 5. 최종적으로 결정된 데이터 상태를 UI에 반영합니다.
        this.updateBorderInputsUI();
    }

    updateBorderInputsUI() {
        // borderWidth 입력 필드 업데이트
        if (this.inputs.borderWidth) {
            const bwData = this.data.borderWidth || {};
            const sides = ['top', 'right', 'bottom', 'left'];

            // 1. 개별 입력창은 항상 데이터에 따라 업데이트
            sides.forEach(side => {
                if (this.inputs.borderWidth[side]) {
                    this.inputs.borderWidth[side].value = bwData[side] || '';
                }
            });

            // 2. 단일 입력창 상태 결정
            const isUniform = sides.every(s => bwData[s] && bwData[s] === bwData.top);
            const isEmpty = Object.values(bwData).every(v => !v);

            if (isEmpty) {
                // ✨ 데이터가 완전히 비었으면, 단일 입력창도 비운다. (가장 중요)
                this.inputs.borderWidth.single.value = '';
            } else if (isUniform) {
                // 모든 값이 동일하면, 단일 입력창에 그 값을 표시
                this.inputs.borderWidth.single.value = bwData.top;
            } else {
                // 값이 다르지만 비어있지는 않으면, 단일 입력창을 비워둠 (개별 입력 중이라는 의미)
                this.inputs.borderWidth.single.value = '';
            }
        }

        // borderStyle select 요소 업데이트
        if (this.inputs.borderStyle && this.inputs.borderStyle.select) {
            this.inputs.borderStyle.select.value = this.data.borderStyle || '';
        }

        // borderColor 입력 필드 업데이트
        if (this.inputs.borderColor && this.inputs.borderColor.text) {
            this.inputs.borderColor.text.value = this.data.borderColor || '';
            this.inputs.borderColor.color.value = this.data.borderColor || '#ffffff';
        }

        // ✨ borderRadius 입력 필드 업데이트
        if (this.inputs.borderRadius) {
            const brData = this.data.borderRadius || {};
            const sides = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];

            // 1. 개별 입력창 업데이트
            sides.forEach(side => {
                if (this.inputs.borderRadius[side]) {
                    this.inputs.borderRadius[side].value = brData[side] || '';
                }
            });

            // 2. 단일 입력창 상태 결정 (borderWidth와 동일한 로직 적용)
            const isUniform = sides.every(s => brData[s] && brData[s] === brData.topLeft);
            const isEmpty = Object.values(brData).every(v => !v);

            if (isEmpty) {
                this.inputs.borderRadius.single.value = '';
            } else if (isUniform) {
                this.inputs.borderRadius.single.value = brData.topLeft;
            } else {
                this.inputs.borderRadius.single.value = '';
            }
        }
    }


    updateAndSave() {
        this.updateBlockStyle();
        this.block.save();
    }

    resetStyle() {
        // 모든 데이터 초기화
        this.data = this.migrateData({}); // 빈 객체로 초기화

        // DOM 입력 필드 초기화
        Object.values(this.inputs).forEach(propInputs => {
            Object.values(propInputs).forEach(input => {
                if (input) input.value = '';
            });
        });

        this.updateAndSave();
    }

    updateBlockStyle() {
        if (this.blockWrapper) {
            this.applyStyle(this.blockWrapper);
        }
    }

    /**
     * ✨ 4. 새로운 데이터 구조를 기반으로 실제 CSS 스타일을 적용합니다.
     */
    applyStyle(element) {
        if (!element) return;

        // 4면 제어 속성 적용
        this.fourSidedProps.forEach(prop => {
            const values = this.data[prop];
            if (values && Object.values(values).some(v => v)) {
                if (prop === 'borderRadius') {
                    // borderRadius는 개별 속성으로 적용
                    element.style.borderTopLeftRadius = values.topLeft ? `${values.topLeft}px` : '';
                    element.style.borderTopRightRadius = values.topRight ? `${values.topRight}px` : '';
                    element.style.borderBottomRightRadius = values.bottomRight ? `${values.bottomRight}px` : '';
                    element.style.borderBottomLeftRadius = values.bottomLeft ? `${values.bottomLeft}px` : '';
                } else if (prop === 'borderWidth') {
                    // borderWidth도 개별 속성으로 적용
                    element.style.borderTopWidth = values.top ? `${values.top}px` : '';
                    element.style.borderRightWidth = values.right ? `${values.right}px` : '';
                    element.style.borderBottomWidth = values.bottom ? `${values.bottom}px` : '';
                    element.style.borderLeftWidth = values.left ? `${values.left}px` : '';
                } else {
                    // padding, margin은 단축 속성으로 적용
                    element.style[prop] = ['top', 'right', 'bottom', 'left']
                        .map(side => `${values[side] || 0}px`)
                        .join(' ');
                }
            } else if (values) {
                // 값이 모두 비어있으면 스타일 초기화
                if (prop === 'borderRadius') {
                    element.style.borderRadius = '';
                } else if (prop === 'borderWidth') {
                    element.style.borderWidth = '';
                } else {
                    element.style[prop] = '';
                }
            }
        });

        // 기타 스타일 적용
        element.style.color = this.data.textColor || '';
        element.style.backgroundColor = this.data.backgroundColor || '';

        // 테두리 스타일 및 색상 적용 (두께는 위에서 이미 처리됨)
        const { borderStyle, borderColor } = this.data;
        const hasAnyBorderValue = borderStyle || borderColor || Object.keys(this.data.borderWidth || {}).some(k => this.data.borderWidth[k]);

        if (hasAnyBorderValue) {
            element.style.borderStyle = borderStyle || this.BORDER_DEFAULTS.style;
            element.style.borderColor = borderColor || this.BORDER_DEFAULTS.color;
        } else {
            element.style.borderStyle = '';
            element.style.borderColor = '';
        }

        // box-sizing은 패딩이나 테두리가 있을 때 항상 필요
        if (element.style.padding || hasAnyBorderValue) {
            element.style.boxSizing = 'border-box';
        } else {
            element.style.boxSizing = '';
        }
        // borderRadius가 있으면 overflow: hidden 처리
        element.style.overflow = Object.keys(this.data.borderRadius || {}).length > 0 ? 'hidden' : '';
    }

    save() {
        const dataToSave = { ...this.data };

        // 4면 제어 속성 최적화 (이전과 동일)
        this.fourSidedProps.forEach(prop => {
            const values = dataToSave[prop];
            if (typeof values === 'object' && values !== null) {
                const sides = prop === 'borderRadius'
                    ? ['topLeft', 'topRight', 'bottomRight', 'bottomLeft']
                    : ['top', 'right', 'bottom', 'left'];

                if (sides.every(s => !values[s])) {
                    delete dataToSave[prop];
                    return;
                }

                if (sides.every(s => values[s] && values[s] === values[sides[0]])) {
                    dataToSave[prop] = values[sides[0]]; // 모든 값이 같으면 단일 값으로 저장
                }
            }
        });

        // 테두리 데이터 정리 로직 강화
        const { borderWidth, borderStyle, borderColor } = dataToSave;
        // 저장 시점의 borderWidth 값 존재 여부 확인 (단일값 또는 객체값)
        const hasBorderWidth = borderWidth && (typeof borderWidth === 'string' || Object.values(borderWidth).some(v => v));
        const hasAnyBorderValue = hasBorderWidth || borderStyle || borderColor;

        if (hasAnyBorderValue) {
            // 값이 하나라도 있으면, 없는 값은 기본값으로 채워서 저장
            if (!dataToSave.borderStyle) dataToSave.borderStyle = this.BORDER_DEFAULTS.style;
            if (!dataToSave.borderColor) dataToSave.borderColor = this.BORDER_DEFAULTS.color;
            // borderWidth는 단일값 기본값으로 저장
            if (!hasBorderWidth) dataToSave.borderWidth = this.BORDER_DEFAULTS.width;
        } else {
            // 값이 하나도 없으면 관련 키를 모두 제거하여 데이터를 깨끗하게 유지
            delete dataToSave.borderWidth;
            delete dataToSave.borderStyle;
            delete dataToSave.borderColor;
        }

        // 최종적으로 빈 값을 가진 키는 저장하지 않음 (이전과 동일)
        return Object.fromEntries(
            Object.entries(dataToSave).filter(([key, value]) => {
                if (value === null || value === undefined || value === '') return false;
                if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
                return true;
            })
        );
    }
}